import type {
  WebsocketDirectAgentMetadata,
  WebsocketHttpFirstAgentMetadata,
} from "@/types/backend-contracts"

type NormalizedJsonTextValue = string | Record<string, unknown> | null
type NullableString = string | null
type NullableNumber = number | null
type NullableBoolean = boolean | null

export interface WebSocketSimulatorConfigData {
  endpoint?: NullableString
  initialization_json?: NormalizedJsonTextValue
  authorization_header?: NullableString
  custom_headers?: NormalizedJsonTextValue
  send_sample_rate_hertz?: NullableNumber
  receive_sample_rate_hertz?: NullableNumber
  pipeline_sample_rate_hertz?: NullableNumber
  handshake_ready_message_type?: NullableString
  handshake_requires_session_id?: NullableBoolean
  handshake_timeout_seconds?: NullableNumber
  send_audio_template?: NullableString
  message_type_path?: NullableString
  audio_message_type_value?: NullableString
  audio_data_path?: NullableString
  audio_encoding?: NullableString
  receive_audio_channels?: NullableNumber
  non_audio_event_message_types?: string[] | null
  websocket_compat_profile?: NullableString
  send_media_template?: NullableString
  send_dtmf_template?: NullableString
  audio_codec?: NullableString
  marks_enabled?: NullableBoolean
  speech_activity_enabled?: NullableBoolean
  connection_mode?: WebsocketDirectAgentMetadata["connection_mode"] | string
  http_url?: WebsocketHttpFirstAgentMetadata["http_url"]
  http_method?: WebsocketHttpFirstAgentMetadata["http_method"] | string
  http_request_body?: NormalizedJsonTextValue
  http_headers?: NormalizedJsonTextValue
  websocket_url_response_path?: WebsocketHttpFirstAgentMetadata["websocket_url_response_path"]
  voice_url?: NullableString
  voice_http_method?: NullableString
  voice_http_headers?: NormalizedJsonTextValue
  voice_form_fields?: NormalizedJsonTextValue
  twilio_account_sid?: NullableString
  twilio_from_number?: NullableString
  twilio_to_number?: NullableString
}

export interface WebSocketFieldProps {
  metadata: WebSocketSimulatorConfigData
  onMetadataUpdate: (metadata: Partial<WebSocketSimulatorConfigData>) => void
  disabled?: boolean
}

export const SAMPLE_RATE_OPTIONS = [
  { value: 8000, label: "8,000 Hz" },
  { value: 16000, label: "16,000 Hz" },
  { value: 24000, label: "24,000 Hz" },
  { value: 48000, label: "48,000 Hz" },
]

export const HTTP_METHOD_OPTIONS = [
  { value: "GET", label: "GET" },
  { value: "POST", label: "POST" },
  { value: "PUT", label: "PUT" },
  { value: "PATCH", label: "PATCH" },
  { value: "DELETE", label: "DELETE" },
  { value: "HEAD", label: "HEAD" },
  { value: "OPTIONS", label: "OPTIONS" },
]

export const TWIML_HTTP_METHOD_OPTIONS = HTTP_METHOD_OPTIONS.filter(
  ({ value }) => value === "GET" || value === "POST",
)

export const RECEIVE_AUDIO_CHANNELS_OPTIONS = [
  { value: 1, label: "Mono" },
  { value: 2, label: "Stereo" },
] as const

// The simulator treats a missing value as stereo.
export const DEFAULT_RECEIVE_AUDIO_CHANNELS = 2

export const AUDIO_ENCODING_OPTIONS = [
  { value: "pcm", label: "PCM (raw)" },
  { value: "mp3", label: "MP3" },
  { value: "ulaw", label: "G.711 mu-law" },
]

export const DEFAULT_AUDIO_TEMPLATE = `{
  "type": "audio_chunk",
  "data": "{{audio_data}}"
}`

export const JSON_AUDIO_TEMPLATE = `{
  "action": "audio_message",
  "payload": {},
  "audio_bytes": "{{audio_data}}",
  "sender": "USER"
}`

export const WEBSOCKET_COMPAT_PROFILE_GENERIC = "generic"
export const WEBSOCKET_COMPAT_PROFILE_JSON_AUDIO = "json_audio"

// These are JSON audio message types read from the configured message_type_path.
// Concrete names such as ocb:cart-updated remain in the event field.
export const JSON_AUDIO_NON_AUDIO_EVENT_MESSAGE_TYPES = [
  "system_notify",
] as const

export const DEFAULT_MEDIA_TEMPLATE = `{
  "type": "media",
  "name": "{{media_name}}",
  "mime_type": "{{mime_type}}",
  "data": "{{media_data}}"
}`

export const DEFAULT_DTMF_TEMPLATE = `{
  "type": "dtmf",
  "digit": "{{digit}}"
}`

// Twilio Media Streams frame shapes. {{stream_sid}} and {{call_sid}} are minted
// per simulation by the backend, so nothing here needs a pasted identifier.
export const TWILIO_AUDIO_TEMPLATE = `{
  "event": "media",
  "streamSid": "{{stream_sid}}",
  "media": {
    "payload": "{{audio_data}}"
  }
}`

export const TWILIO_DTMF_TEMPLATE = `{
  "event": "dtmf",
  "streamSid": "{{stream_sid}}",
  "dtmf": {
    "track": "inbound_track",
    "digit": "{{digit}}"
  }
}`

export const TWILIO_INITIALIZATION_JSON = JSON.stringify(
  {
    event: "start",
    streamSid: "{{stream_sid}}",
    start: {
      streamSid: "{{stream_sid}}",
      callSid: "{{call_sid}}",
      tracks: ["inbound"],
      customParameters: {},
      mediaFormat: {
        encoding: "audio/x-mulaw",
        sampleRate: 8000,
        channels: 1,
      },
    },
  },
  null,
  2,
)
